import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

export const magazines = [
  {
    id: "cedar-field",
    title: "Cedar & Field",
    subtitle: "Q3 2026 · Issue 07",
    pages: [
      { img: "/mag/real/1.jpg", title: "Cover" },
      { img: "/mag/real/2.jpg", title: "The Quarter at a Glance" },
      { img: "/mag/real/3.jpg", title: "Key Updates" },
      { img: "/mag/real/4.jpg", title: "Decisions & What Changes" },
      { img: "/mag/real/5.jpg", title: "Numbers with Context" },
      { img: "/mag/real/6.jpg", title: "People Spotlight" },
      { img: "/mag/real/7.jpg", title: "What Happens Next" },
      { img: "/mag/real/8.jpg", title: "Resources & Questions" },
    ],
  },
  {
    id: "city-current",
    title: "Harborline Mobility",
    subtitle: "City Current · Issue 08",
    pages: [
      { img: "/mag/citycurrent/1.jpg", title: "Cover" },
      { img: "/mag/citycurrent/2.jpg", title: "The Quarter at a Glance" },
      { img: "/mag/citycurrent/3.jpg", title: "Key Updates" },
      { img: "/mag/citycurrent/4.jpg", title: "Decisions & What Changes" },
      { img: "/mag/citycurrent/5.jpg", title: "Numbers with Context" },
      { img: "/mag/citycurrent/6.jpg", title: "People Spotlight" },
      { img: "/mag/citycurrent/7.jpg", title: "What Happens Next" },
      { img: "/mag/citycurrent/8.jpg", title: "Resources & Questions" },
    ],
  },
  {
    id: "northstar",
    title: "Northstar Mobility",
    subtitle: "Q4 2026 · Issue 08",
    pages: [
      { img: "/mag/northstar/1.jpg", title: "Cover" },
      { img: "/mag/northstar/2.jpg", title: "The Quarter at a Glance" },
      { img: "/mag/northstar/3.jpg", title: "Key Updates" },
      { img: "/mag/northstar/4.jpg", title: "Decisions & What Changes" },
      { img: "/mag/northstar/5.jpg", title: "Numbers with Context" },
      { img: "/mag/northstar/6.jpg", title: "People Spotlight" },
      { img: "/mag/northstar/7.jpg", title: "What Happens Next" },
      { img: "/mag/northstar/8.jpg", title: "Resources & Questions" },
    ],
  },
];

export const FlipbookModal = ({ open, onClose, openLead, magazine }) => {
  const pages = magazine?.pages || [];
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);

  const go = (d) => {
    setIndex((i) => {
      const n = i + d;
      if (n < 0 || n >= pages.length) return i;
      setDir(d);
      return n;
    });
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const page = pages[index];
  if (!page) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex flex-col items-center justify-center bg-walnut/75 p-4 backdrop-blur-md"
          onClick={onClose}
          data-testid="flipbook-overlay"
        >
          <button
            data-testid="flipbook-close-button"
            onClick={onClose}
            aria-label="Close flipbook"
            className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-walnut"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="mb-4 flex w-full items-center justify-between px-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/70">
            <span>
              {magazine.title} — {magazine.subtitle}
            </span>
            <span data-testid="flipbook-counter">
              {String(index + 1).padStart(2, "0")} / {String(pages.length).padStart(2, "0")}
            </span>
          </div>

          <div
            className="relative h-[68vh] [perspective:1400px]"
            onClick={(e) => e.stopPropagation()}
            data-testid="flipbook-modal"
          >
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={index}
                initial={{ x: dir * 90, rotateY: dir * -45, opacity: 0 }}
                animate={{ x: 0, rotateY: 0, opacity: 1 }}
                exit={{ x: dir * -90, rotateY: dir * 45, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid="flipbook-page"
                className="relative h-full overflow-hidden rounded-xl border border-paper/15 shadow-[0_40px_90px_rgba(0,0,0,0.45)]"
              >
                <img
                  src={page.img}
                  alt={`${magazine.title} — ${page.title}`}
                  className="h-full w-auto object-contain"
                />
                {index === pages.length - 1 && (
                  <div className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-walnut/70 to-transparent px-6 pb-6 pt-14">
                    <button
                      data-testid="flipbook-cta-button"
                      onClick={() => {
                        onClose();
                        openLead();
                      }}
                      className="group flex items-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terracotta-dark"
                    >
                      Get Your Own Issue
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center gap-6" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="flipbook-prev-button"
              onClick={() => go(-1)}
              disabled={index === 0}
              aria-label="Previous page"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-walnut disabled:opacity-30"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2" data-testid="flipbook-dots">
              {pages.map((p, i) => (
                <span
                  key={p.img}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-terracotta" : "w-1.5 bg-paper/40"}`}
                />
              ))}
            </div>
            <button
              data-testid="flipbook-next-button"
              onClick={() => go(1)}
              disabled={index === pages.length - 1}
              aria-label="Next page"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors duration-300 hover:bg-paper hover:text-walnut disabled:opacity-30"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50">
            Fictional sample — not a real client
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
